import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import DashboardContainer from "./DashboardContainer";
import { FiGlobe } from "react-icons/fi";

const API_URL = "https://api.myad360.com/api/v1/FNV89uJ9sud/rest_api/get_user_domain.php";
const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

// ── Status Badge ───────────────────────────────────────────────────────────
const STATUS_MAP = {
    "1": { label: "Approve", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
    "2": { label: "Rejected", bg: "bg-red-50 border-red-200", text: "text-red-700", dot: "bg-red-500" },
    "0": { label: "Pending", bg: "bg-amber-50 border-amber-200", text: "text-amber-700", dot: "bg-amber-400" },
};

function StatusBadge({ status }) {
    const s = STATUS_MAP[String(status)] ?? {
        label: "Unknown", bg: "bg-gray-50 border-gray-200", text: "text-gray-600", dot: "bg-gray-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.bg} ${s.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {s.label}
        </span>
    );
}

// ── Icons ──────────────────────────────────────────────────────────────────
const ChevronFirst = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
    </svg>
);
const ChevronLast = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
    </svg>
);
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);
const ChevronRight = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

function NavBtn({ onClick, disabled, children }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`p-1.5 rounded-lg transition-colors duration-150 ${disabled
                ? "text-gray-300 cursor-not-allowed"
                : "text-[#8A7560] hover:text-[#D97706] hover:bg-[#FFF4E5]"
                }`}
        >
            {children}
        </button>
    );
}

function SkeletonRow() {
    return (
        <tr className="border-b border-[#FDE8CD] animate-pulse">
            {[...Array(5)].map((_, i) => (
                <td key={i} className="px-4 py-3.5">
                    <div className="h-4 bg-[#FDE8CD]/50 rounded-md w-3/4" />
                </td>
            ))}
        </tr>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function DomainTable() {
    // ✅ Same pattern as Adddomain.jsx — token from Redux store
    const { userLoginToken } = useSelector((state) => state.user);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        // Guard: no token in Redux store yet
        if (!userLoginToken) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": userLoginToken,   // ✅ dynamic from Redux — same as Adddomain.jsx
                },
                body: JSON.stringify({ perpage: pageSize, page }),
            });

            if (!res.ok) throw new Error(`Server error: ${res.status} ${res.statusText}`);

            // Read as text first so we can show a helpful message on parse failure
            const text = await res.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch {
                setRows([]);
                setTotalCount(0);
                return;
            }

            // ── Normalize to array ─────────────────────────────────────
            let data = [];
            if (Array.isArray(json)) {
                // ✅ API returns a plain array directly
                data = json;
            } else if (json.data && Array.isArray(json.data)) {
                data = json.data;
                if (json.total) setTotalCount(Number(json.total));
                if (json.total_count) setTotalCount(Number(json.total_count));
            } else if (json.result && Array.isArray(json.result)) {
                data = json.result;
                if (json.total_count) setTotalCount(Number(json.total_count));
            } else {
                // Handle status 0, Data Not Found, or any non-array response as empty state cleanly
                const firstArray = Object.values(json).find(Array.isArray);
                if (firstArray) {
                    data = firstArray;
                } else {
                    data = [];
                }
            }

            setRows(data);

            // Infer pagination when API gives no total count
            if (!json.total && !json.total_count) {
                const seen = page * pageSize + data.length;
                setHasMore(data.length === pageSize);
                setTotalCount((prev) => Math.max(prev, seen));
            }

        } catch (err) {
            console.error("DomainTable fetch error:", err);
            setError("Failed to load data from server. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, userLoginToken]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Derived pagination ─────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const isFirstPage = page === 0;
    const isLastPage = page >= totalPages - 1 || !hasMore;
    const startEntry = totalCount === 0 ? 0 : page * pageSize + 1;
    const endEntry = Math.min((page + 1) * pageSize, totalCount);

    function goToPage(p) { setPage(Math.max(0, p)); }

    function handlePageSize(e) {
        setPageSize(Number(e.target.value));
        setPage(0);
        setTotalCount(0);
        setHasMore(true);
    }

    return (
        <DashboardContainer>
            <div className="p-2 sm:p-4">
                {/* ── Section Header ── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-[#F0DCC8]">
                    <div className="flex items-center gap-2.5">
                        <h3 className="font-bold text-base sm:text-lg text-[#1C1410]">Your Domains List</h3>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FFF4E5] text-[#D97706] border border-[#FDE8CD]">
                            {totalCount} {totalCount === 1 ? "Domain" : "Domains"}
                        </span>
                    </div>

                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D97706] hover:text-[#EA580C] hover:bg-[#FFF4E5] px-3 py-1.5 rounded-lg border border-[#FDE8CD] transition-colors self-start sm:self-auto"
                    >
                        <svg className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh List
                    </button>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                        <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-red-700">Failed to load data</p>
                            <p className="text-xs text-red-500 mt-0.5 break-words">{error}</p>
                        </div>
                        <button
                            onClick={fetchData}
                            className="ml-2 text-xs text-red-600 underline hover:text-red-800 whitespace-nowrap shrink-0"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* ── Table ── */}
                <div className="w-full border border-[#FDE8CD] rounded-2xl overflow-hidden shadow-sm bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-[#FDE8CD] bg-gradient-to-r from-[#FFF4E5] to-[#FEF3E5]">
                                    {["App Name", "Domain Name", "Full Name", "User Email", "Status"].map((h) => (
                                        <th key={h} className="px-4 py-3.5 text-left text-xs font-bold text-[#1C1410] uppercase tracking-wider whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    [...Array(Math.min(pageSize, 5))].map((_, i) => <SkeletonRow key={i} />)
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-[#8A7560]">
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <FiGlobe className="w-8 h-8 text-[#D97706]/40" />
                                                <span>No registered domains found. Add your first domain above!</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, idx) => {
                                        const rawUrl = row.domain_name ?? "";
                                        const shortUrl = rawUrl.replace(/^https?:\/\//, "");
                                        return (
                                            <tr
                                                key={row.domain_id ?? idx}
                                                className="border-b border-[#FDE8CD] last:border-b-0 hover:bg-[#FFFBF5] transition-colors duration-150"
                                            >
                                                <td className="px-4 py-3.5 text-sm text-[#1C1410] font-semibold">{row.app_name ?? "—"}</td>
                                                <td className="px-4 py-3.5 text-sm text-[#1C1410]">
                                                    {rawUrl ? (
                                                        <a
                                                            href={rawUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-[#D97706] hover:text-[#EA580C] hover:underline inline-flex items-center gap-1 font-medium transition-colors"
                                                            title={rawUrl}
                                                        >
                                                            <span>{shortUrl}</span>
                                                            <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </a>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-4 py-3.5 text-sm text-[#8A7560]">{row.full_name ?? "—"}</td>
                                                <td className="px-4 py-3.5 text-sm text-[#8A7560]">{row.email ?? "—"}</td>
                                                <td className="px-4 py-3.5"><StatusBadge status={row.domain_status} /></td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Pagination Footer ── */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-1">
                    <span className="text-xs sm:text-sm text-[#8A7560]">
                        {loading ? "Loading entries…" : `Showing ${startEntry}–${endEntry} of ${totalCount} entries`}
                    </span>

                    <div className="flex items-center gap-4">
                        {/* Rows per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs sm:text-sm text-[#8A7560] whitespace-nowrap">Rows per page:</span>
                            <div className="relative">
                                <select
                                    value={pageSize}
                                    onChange={handlePageSize}
                                    disabled={loading}
                                    className="appearance-none text-xs sm:text-sm text-[#1C1410] bg-[#FFF4E5] border border-[#FDE8CD] rounded-lg pr-6 pl-2.5 py-1 outline-none cursor-pointer font-semibold disabled:opacity-50"
                                >
                                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <svg className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#D97706]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>

                        {/* Nav buttons */}
                        <div className="flex items-center gap-1">
                            <NavBtn onClick={() => goToPage(0)} disabled={loading || isFirstPage}><ChevronFirst /></NavBtn>
                            <NavBtn onClick={() => goToPage(page - 1)} disabled={loading || isFirstPage}><ChevronLeft /></NavBtn>
                            <NavBtn onClick={() => goToPage(page + 1)} disabled={loading || isLastPage}><ChevronRight /></NavBtn>
                            <NavBtn onClick={() => goToPage(totalPages - 1)} disabled={loading || isLastPage}><ChevronLast /></NavBtn>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardContainer>
    );
}